/**
 * Relateds-specific composable following SOLID principles
 * Complete refactored version with proper dependency injection
 */
import { onMounted, computed } from '@vue/composition-api';
import { useGamePage } from './useGamePage';
import { useDiscovery } from './useDiscovery';
import { useReceivedEventSubscription } from './useEventBusSubscription';
import { useRelatedsData } from './relateds/useRelatedsData';
import { useRelatedsApi } from './relateds/useRelatedsApi';
import { handleAsync } from '../services/ErrorHandling';
import { useTutorial } from './tutorial';

/**
 * Composable for related page functionality
 * @param {Object} props - Component props (memberLineId, groupId, etc.)
 * @returns {Object} Related state and methods
 */
export function useRelateds(props) {
  // Tutorial integration
  const tutorial = useTutorial('relateds', {
    memberLineId: props.memberLineId,
    groupId: props.groupId
  });

  // Base game page functionality
  const gamePageState = useGamePage('related', {
    bannerStyles: "top: 70px; left: 155px;"
  });

  // Discovery management
  const discoveryState = useDiscovery();

  // Relateds data management
  const relatedsDataState = useRelatedsData();

  // API operations
  const relatedsApi = useRelatedsApi();
  
  /**
   * Load relateds data from API with error handling
   */
  const loadRelatedsData = async () => {
    const [error, data] = await handleAsync(
      relatedsApi.loadRelatedsData({
        groupId: props.groupId,
        memberLineId: props.memberLineId
      }),
      { operation: 'loadRelatedsData', component: 'useRelateds' }
    );

    if (error) {
      throw error;
    }

    // Initialize game state
    const gameData = gamePageState.initGameData(data);
    discoveryState.initDiscoveryData(gameData);

    // Initialize relateds data
    relatedsDataState.initRelatedsData(gameData);

    // Set progress tracking
    const hadFound = gameData.hadFound || {};
    relatedsDataState.updateFoundItems('exhibit', hadFound.exhibitIds || []);
    relatedsDataState.updateFoundItems('related', hadFound.relatedIds || []);
    relatedsDataState.updateFoundItems('position', hadFound.positionIds || []);
    relatedsDataState.updateFoundItems('timeline', hadFound.timelineIds || []);

    // Handle demo completion
    if (gameData.demo && props.updateFinishedDemo) {
      props.updateFinishedDemo();
    }

    return gameData;
  };

  /**
   * Search for related person using simple logic
   * @param {string} queryText - Search query
   */
  const searchRelated = async (queryText) => {
    const targetRelated = relatedsDataState.findRelatedByQuery(queryText);

    // Check if already found or default
    if (targetRelated && (gamePageState.progress.value.includes(targetRelated.id) || targetRelated.status === 'default')) {
      relatedsDataState.setSelectRelated(targetRelated);
      // Advance tutorial if waiting for search action
      advanceTutorialOnSearchWitness(queryText);
      return targetRelated;
    }

    // Update story ship (always call API, even for wrong names)
    if (props.updateGroupStoryShip) {
      props.updateGroupStoryShip('related', queryText);
    }

    // If no match found, return early after API call
    if (!targetRelated) {
      // Still check tutorial for search action
      advanceTutorialOnSearchWitness(queryText);
      return null;
    }

    // Update found list
    updateRelated(targetRelated);
    relatedsDataState.setSelectRelated(targetRelated);

    // Advance tutorial if waiting for search action
    advanceTutorialOnSearchWitness(queryText);

    return targetRelated;
  };

  /**
   * Update found relateds list
   * @param {Object} newItem - New related item
   */
  const updateRelated = (newItem) => {
    if (gamePageState.progress.value.includes(newItem.id)) return;
    
    gamePageState.progress.value.push(newItem.id);
    
    // Mark related as found
    const relatedIndex = relatedsDataState.relateds.value.findIndex(r => r.id === newItem.id);
    if (relatedIndex !== -1) {
      relatedsDataState.relateds.value[relatedIndex].found = true;
    }
  };

  /**
   * Change selected related by index
   * @param {number} index - Related index
   */
  const changeRelated = (index) => {
    relatedsDataState.found.value = true;
    const selectedRelated = relatedsDataState.relateds.value[index];
    relatedsDataState.setSelectRelated(selectedRelated);

    // Advance tutorial if waiting for witness selection
    advanceTutorialOnSelectWitness(selectedRelated);
  };

  /**
   * Helper to advance tutorial when user selects a witness
   */
  const advanceTutorialOnSelectWitness = (selectedRelated) => {
    if (tutorial.isActive.value &&
        tutorial.currentStep.value &&
        tutorial.currentStep.value.waitForAction === 'select-witness') {
      // Validate that user clicked the correct witness
      const expectedWitness = '王怡晨';
      if (selectedRelated && selectedRelated.name !== expectedWitness) {
        alert(`請點擊「${expectedWitness}」來繼續教學！`);
        return;
      }
      setTimeout(() => {
        tutorial.nextStep();
      }, 300);
    }
  };

  /**
   * Helper to advance tutorial when user searches for a witness
   */
  const advanceTutorialOnSearchWitness = (queryText) => {
    if (tutorial.isActive.value &&
        tutorial.currentStep.value &&
        tutorial.currentStep.value.waitForAction === 'search-witness') {
      // Validate that user typed the correct search term
      const expectedTerm = '周百樂';
      if (queryText !== expectedTerm) {
        alert(`請輸入「${expectedTerm}」來繼續教學！`);
        return;
      }
      setTimeout(() => {
        tutorial.nextStep();
      }, 300);
    }
  };

  /**
   * Computed property for tutorial highlight clue
   * Shows '周百樂' highlight when on the found-clue step
   */
  const tutorialHighlightClue = computed(() => {
    if (tutorial.isActive.value && tutorial.currentStep.value) {
      const stepId = tutorial.currentStep.value.id;
      // Show highlight on found-clue step and search-witness step
      if (stepId === 'found-clue' || stepId === 'search-witness') {
        return '周百樂';
      }
    }
    return '';
  });

  /**
   * Handle received items from event bus
   * @param {string} itemType - Item type
   * @param {Object} item - Item data
   */
  const updateReceivedHadFoundItem = (itemType, item) => {
    if (!item) return;
    
    // Update the corresponding array based on item type
    switch(itemType) {
      case 'hadFoundExhibitIds':
        relatedsDataState.updateFoundItems('exhibit', [item]);
        break;
      case 'hadFoundRelatedIds':
        relatedsDataState.updateFoundItems('related', [item]);
        break;
      case 'hadFoundTimelineIds':
        relatedsDataState.updateFoundItems('timeline', [item]);
        break;
      case 'hadFoundPositionIds':
        relatedsDataState.updateFoundItems('position', [item]);
        break;
    }
  };

  // Event bus subscription
  useReceivedEventSubscription(updateReceivedHadFoundItem);

  // Initialize on mount
  onMounted(async () => {
    gamePageState.initViewData();
    const data = await loadRelatedsData();
    gamePageState.completeInit();

    // Initialize tutorial from API response
    if (data) {
      tutorial.initFromApiResponse(data);
    }
  });

  return {
    // State from composables
    ...gamePageState,
    ...discoveryState,
    ...relatedsDataState,

    // Methods
    loadRelatedsData,
    searchRelated,
    changeRelated,
    updateRelated,

    // Tutorial
    tutorial,
    tutorialHighlightClue
  };
}